import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/datetime";
import { isValidRut } from "../../lib/rut";
import RutInput from "../../components/RutInput";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  FileText,
  Download,
  X,
  Star,
  ClipboardList,
  Check,
} from "lucide-react";

const STATUSES = [
  { value: "draft", label: "Borrador" },
  { value: "sent", label: "Enviada" },
  { value: "accepted", label: "Aceptada" },
  { value: "rejected", label: "Rechazada" },
  { value: "expired", label: "Expirada" },
];

const EMPTY = {
  client_name: "",
  client_email: "",
  client_company: "",
  client_phone: "",
  client_rut: "",
  client_address: "",
  project_title: "",
  project_description: "",
  items: [{ description: "", quantity: 1, unit_price: "", discount_percent: "", billing: "one_time" }],
  currency: "CLP",
  tax_rate: 19,
  notes: "",
  terms: "",
  valid_until: defaultValidUntil(),
};

function defaultValidUntil() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

export default function AdminProposalEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const fromQuote = searchParams.get("from");
  const nav = useNavigate();

  const isNew = id === undefined;
  const [form, setForm] = useState(EMPTY);
  const [number, setNumber] = useState(null);
  const [status, setStatus] = useState("draft");
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsTemplates, setTermsTemplates] = useState([]);

  // Load existing or seed from quote
  useEffect(() => {
    if (id) {
      api.get(`/admin/proposals/${id}`).then((r) => {
        const d = r.data;
        setForm({
          client_name: d.client_name || "",
          client_email: d.client_email || "",
          client_company: d.client_company || "",
          client_phone: d.client_phone || "",
          client_rut: d.client_rut || "",
          client_address: d.client_address || "",
          project_title: d.project_title || "",
          project_description: d.project_description || "",
          items: d.items?.length ? d.items.map(it => ({ ...it, billing: it.billing || "one_time", discount_percent: it.discount_percent || "" })) : [{ description: "", quantity: 1, unit_price: 0, discount_percent: "", billing: "one_time" }],
          currency: d.currency || "USD",
          tax_rate: d.tax_rate || 0,
          notes: d.notes || "",
          terms: d.terms || "",
          valid_until: d.valid_until ? d.valid_until.slice(0, 10) : "",
        });
        setNumber(d.number);
        setStatus(d.status);
        setCreated(d.created_at);
        setLoading(false);
      });
    } else if (fromQuote) {
      api.get("/admin/quotes").then((r) => {
        const q = r.data.find((x) => x.id === fromQuote);
        if (q) {
          setForm((f) => ({
            ...f,
            client_name: q.name,
            client_email: q.email,
            client_company: q.company || "",
            client_phone: q.phone || "",
            project_title: (q.project_type || "Propuesta").replace(/\s*\([^)]*\)\s*$/, "").trim(),
            project_description: q.description || "",
          }));
        }
      });
    }
  }, [id, fromQuote]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load Terms & Conditions templates once
  useEffect(() => {
    api.get("/admin/terms-templates").then((r) => setTermsTemplates(r.data)).catch(() => {});
  }, []);

  const applyTermsTemplate = (tpl, mode = "replace") => {
    setForm((f) => ({
      ...f,
      terms: mode === "append" && f.terms ? `${f.terms}\n\n${tpl.content}` : tpl.content,
    }));
    setTermsModalOpen(false);
    toast.success(`Plantilla "${tpl.title}" aplicada`);
  };
  const updateItem = (idx, k, v) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)),
    }));
  const addItem = () =>
    setForm((f) => ({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: "", discount_percent: "", billing: "one_time" }] }));
  const removeItem = (idx) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const computeBucket = (billing) => {
    let gross = 0, discount = 0;
    form.items.forEach((it) => {
      const b = it.billing === "monthly" ? "monthly" : "one_time";
      if (b !== billing) return;
      const g = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
      const d = g * (Number(it.discount_percent) || 0) / 100;
      gross += g;
      discount += d;
    });
    const net = gross - discount;
    const taxRate = Number(form.tax_rate) || 0;
    const tax = net * taxRate / 100;
    return { gross, discount, net, tax, total: net + tax };
  };
  const otBucket = computeBucket("one_time");
  const moBucket = computeBucket("monthly");
  const taxRate = Number(form.tax_rate) || 0;
  const hasMonthly = otBucket.gross > 0 ? moBucket.gross > 0 : true;
  const hasAnyDiscount = otBucket.discount > 0 || moBucket.discount > 0;

  const fmtMoney = (v) =>
    form.currency === "CLP"
      ? `$${Math.round(v).toLocaleString("es-CL")}`
      : `US$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const save = async () => {
    if (form.client_rut && !isValidRut(form.client_rut)) {
      toast.error("El RUT del cliente no es válido");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items
          .filter((it) => it.description.trim())
          .map((it) => ({
            description: it.description,
            quantity: Number(it.quantity) || 0,
            unit_price: Number(it.unit_price) || 0,
            discount_percent: Number(it.discount_percent) || 0,
            billing: it.billing === "monthly" ? "monthly" : "one_time",
          })),
        tax_rate: Number(form.tax_rate) || 0,
        valid_until: form.valid_until ? new Date(form.valid_until + "T00:00:00-04:00").toISOString() : null,
      };

      if (isNew) {
        if (fromQuote) payload.quote_id = fromQuote;
        const r = await api.post("/admin/proposals", payload);
        toast.success(`Propuesta ${r.data.number} creada`);
        nav("/admin/proposals");
      } else {
        await api.patch(`/admin/proposals/${id}`, { ...payload, status });
        toast.success("Propuesta actualizada");
        nav("/admin/proposals");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const openPdf = async () => {
    const r = await api.get(`/admin/proposals/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    window.open(url, "_blank");
  };

  const downloadPdf = async () => {
    const r = await api.get(`/admin/proposals/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `propuesta-${number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="p-10 text-ash-300">Cargando…</div>;

  return (
    <div data-testid="proposal-editor" className="p-6 md:p-8 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5">
        <div>
          <button onClick={() => nav("/admin/proposals")} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ash-300 hover:text-brand mb-2">
            <ArrowLeft size={14} /> Propuestas
          </button>
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className="font-display font-bold text-2xl text-ash-100">
              {isNew ? "Nueva propuesta" : `Propuesta ${number}`}
            </h1>
            {!isNew && (
              <>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  data-testid="proposal-status-select"
                  className="bg-ink-800 border border-ink-500 text-ash-100 px-3 py-2 text-xs uppercase tracking-widest font-mono focus:border-brand outline-none"
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <span className="text-xs text-ash-500 font-mono">
                  Creada: {formatDate(created)}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {!isNew && (
            <>
              <button onClick={openPdf} data-testid="proposal-open-pdf" className="inline-flex items-center gap-2 border border-ink-500 hover:border-brand px-4 py-3 text-xs uppercase tracking-widest text-ash-100 transition-colors">
                <FileText size={14} /> Ver PDF
              </button>
              <button onClick={downloadPdf} data-testid="proposal-download-pdf" className="inline-flex items-center gap-2 border border-ink-500 hover:border-brand px-4 py-3 text-xs uppercase tracking-widest text-ash-100 transition-colors">
                <Download size={14} /> Descargar PDF
              </button>
            </>
          )}
          <button onClick={save} disabled={saving} data-testid="proposal-save" className="inline-flex items-center gap-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-5 py-3 uppercase tracking-wider text-xs disabled:opacity-50">
            <Save size={14} /> {saving ? "Guardando…" : "Guardar propuesta"}
          </button>
        </div>
      </div>

      {/* Grid: Client + Project */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-ink-500 border border-ink-500 mb-3">
        <Section title="Cliente">
          <FieldGrid>
            <Field label="Nombre *">
              <input className={inputCls} value={form.client_name} onChange={(e) => update("client_name", e.target.value)} />
            </Field>
            <Field label="Email *">
              <input className={inputCls} value={form.client_email} onChange={(e) => update("client_email", e.target.value)} />
            </Field>
            <Field label="Empresa">
              <input className={inputCls} value={form.client_company} onChange={(e) => update("client_company", e.target.value)} />
            </Field>
            <Field label="Teléfono">
              <input className={inputCls} value={form.client_phone} onChange={(e) => update("client_phone", e.target.value)} />
            </Field>
            <Field label="RUT Empresa">
              <RutInput
                value={form.client_rut}
                onChange={(v) => update("client_rut", v)}
                className={inputCls}
                testId="proposal-client-rut"
              />
            </Field>
            <Field label="Dirección">
              <input className={inputCls} value={form.client_address} onChange={(e) => update("client_address", e.target.value)} />
            </Field>
          </FieldGrid>
        </Section>

        <Section title="Proyecto">
          <Field label="Título del proyecto *">
            <input className={inputCls} value={form.project_title} onChange={(e) => update("project_title", e.target.value)} />
          </Field>
          <div className="h-3" />
          <Field label="Descripción">
            <textarea rows={3} className={inputCls} value={form.project_description} onChange={(e) => update("project_description", e.target.value)} />
          </Field>
          <div className="h-3" />
          <FieldGrid>
            <Field label="Moneda">
              <select className={inputCls} value={form.currency} onChange={(e) => update("currency", e.target.value)}>
                <option value="USD">USD</option>
                <option value="CLP">CLP</option>
                <option value="EUR">EUR</option>
              </select>
            </Field>
            <Field label="Válida hasta">
              <input type="date" className={inputCls} value={form.valid_until} onChange={(e) => update("valid_until", e.target.value)} />
            </Field>
          </FieldGrid>
        </Section>
      </div>

      {/* Items */}
      <Section title="Ítems" full>
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="w-full text-xs min-w-[860px]">
            <thead>
              <tr className="bg-ink-700">
                <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-ash-300 font-mono w-10">#</th>
                <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-ash-300 font-mono">Descripción</th>
                <th className="px-2 py-2 text-center text-[10px] uppercase tracking-widest text-ash-300 font-mono w-28">Tipo</th>
                <th className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-ash-300 font-mono w-20">Cant.</th>
                <th className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-ash-300 font-mono w-32">Precio unit.</th>
                <th className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-ash-300 font-mono w-20">Desc. %</th>
                <th className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-ash-300 font-mono w-32">Subtotal</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => {
                const gross = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                const disc = gross * (Number(it.discount_percent) || 0) / 100;
                const sub = gross - disc;
                const isMonthly = it.billing === "monthly";
                return (
                  <tr key={i} className="border-t border-ink-500">
                    <td className="px-2 py-1.5 text-ash-500 font-mono text-xs">{i + 1}</td>
                    <td className="px-2 py-1.5">
                      <input
                        data-testid={`item-desc-${i}`}
                        className={inputCls}
                        placeholder="Descripción del ítem"
                        value={it.description}
                        onChange={(e) => updateItem(i, "description", e.target.value)}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <select
                        data-testid={`item-billing-${i}`}
                        value={it.billing || "one_time"}
                        onChange={(e) => updateItem(i, "billing", e.target.value)}
                        className={`${inputCls} text-center font-semibold ${isMonthly ? "border-[#D97757] text-[#D97757]" : ""}`}
                      >
                        <option value="one_time">Único</option>
                        <option value="monthly">Mensual</option>
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="number"
                        step="0.01"
                        data-testid={`item-qty-${i}`}
                        className={`${inputCls} text-right`}
                        value={it.quantity}
                        onChange={(e) => updateItem(i, "quantity", e.target.value)}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="number"
                        step="0.01"
                        data-testid={`item-price-${i}`}
                        className={`${inputCls} text-right`}
                        value={it.unit_price}
                        onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        data-testid={`item-disc-${i}`}
                        className={`${inputCls} text-right`}
                        value={it.discount_percent ?? ""}
                        onChange={(e) => updateItem(i, "discount_percent", e.target.value)}
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-ash-100 text-xs whitespace-nowrap">
                      {fmtMoney(sub)}{isMonthly && <span className="text-[#D97757] text-[10px] ml-0.5">/mes</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <button
                        onClick={() => removeItem(i)}
                        disabled={form.items.length === 1}
                        className="text-ash-500 hover:text-destructive disabled:opacity-30"
                        data-testid={`item-remove-${i}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
          <button
            onClick={addItem}
            data-testid="item-add"
            className="inline-flex items-center gap-2 border border-ink-500 hover:border-brand px-3 py-1.5 text-xs uppercase tracking-widest text-ash-100"
          >
            <Plus size={13} /> Agregar ítem
          </button>
          <div className="flex items-center gap-2">
            <span className="text-ash-300 text-xs">Impuesto</span>
            <input
              type="number"
              step="0.01"
              value={form.tax_rate}
              onChange={(e) => update("tax_rate", e.target.value)}
              className="w-16 bg-ink-800 border border-ink-500 text-ash-100 px-2 py-1 text-xs text-right outline-none focus:border-brand"
              data-testid="proposal-tax-rate"
            />
            <span className="text-ash-500 text-xs">%</span>
          </div>
        </div>

        {/* Unified Totals */}
        <div className="mt-4 ml-auto w-full md:w-[560px] border border-ink-500 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="px-3 py-2 bg-ink-700"></th>
                <th className="px-3 py-2 bg-ink-800 text-[10px] uppercase tracking-widest font-mono text-brand text-center border-b-2 border-brand">Pago Único</th>
                <th className="px-3 py-2 bg-ink-800 text-[10px] uppercase tracking-widest font-mono text-[#D97757] text-center border-b-2 border-[#D97757]">Pago Mensual</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-ink-500 bg-ink-700">
                <td className="px-3 py-1.5 text-ash-300">Subtotal</td>
                <td className="px-3 py-1.5 text-right font-mono text-ash-100">{otBucket.gross > 0 ? fmtMoney(otBucket.gross) : "—"}</td>
                <td className="px-3 py-1.5 text-right font-mono text-ash-100">{moBucket.gross > 0 ? `${fmtMoney(moBucket.gross)} /mes` : "—"}</td>
              </tr>
              {hasAnyDiscount && (
                <tr className="border-t border-ink-500 bg-ink-700">
                  <td className="px-3 py-1.5 text-ash-300">Descuento</td>
                  <td className="px-3 py-1.5 text-right font-mono text-[#FF7A7A]">{otBucket.discount > 0 ? `-${fmtMoney(otBucket.discount)}` : "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-[#FF7A7A]">{moBucket.discount > 0 ? `-${fmtMoney(moBucket.discount)} /mes` : "—"}</td>
                </tr>
              )}
              <tr className="border-t border-ink-500 bg-ink-700">
                <td className="px-3 py-1.5 text-ash-300">Impuesto ({taxRate}%)</td>
                <td className="px-3 py-1.5 text-right font-mono text-ash-300">{otBucket.tax > 0 ? fmtMoney(otBucket.tax) : "—"}</td>
                <td className="px-3 py-1.5 text-right font-mono text-ash-300">{moBucket.tax > 0 ? `${fmtMoney(moBucket.tax)} /mes` : "—"}</td>
              </tr>
              <tr className="border-t border-ink-500">
                <td className="px-3 py-2.5 bg-ink-800 font-display font-bold uppercase tracking-widest text-[11px] text-ash-100">Total</td>
                <td className="px-3 py-2.5 bg-brand/15 text-right font-display font-bold text-brand text-sm" data-testid="proposal-total-ot">
                  {otBucket.total > 0 ? fmtMoney(otBucket.total) : "—"}
                </td>
                <td className="px-3 py-2.5 bg-[#D97757]/15 text-right font-display font-bold text-[#D97757] text-sm" data-testid="proposal-total-mo">
                  {moBucket.total > 0 ? `${fmtMoney(moBucket.total)} /mes` : "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* Notes + Terms */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-ink-500 border border-ink-500 mt-3">
        <Section title="Notas">
          <textarea rows={3} className={inputCls} value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Notas internas o aclaraciones para el cliente…" />
        </Section>
        <Section
          title="Términos y condiciones"
          action={
            <button
              type="button"
              data-testid="terms-pick-btn"
              onClick={() => setTermsModalOpen(true)}
              className="inline-flex items-center gap-1.5 border border-ink-500 hover:border-brand hover:text-brand text-ash-300 px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono"
              title={termsTemplates.length ? "Insertar desde plantilla" : "Aún no hay plantillas (admin las crea en /admin/terms)"}
            >
              <ClipboardList size={12} strokeWidth={1.8} /> Plantilla
            </button>
          }
        >
          <textarea rows={3} className={inputCls} value={form.terms} onChange={(e) => update("terms", e.target.value)} />
        </Section>
      </div>

      {termsModalOpen && (
        <TermsPickerModal
          templates={termsTemplates}
          currentTerms={form.terms || ""}
          onPick={applyTermsTemplate}
          onRemove={(tpl) => {
            setForm((f) => {
              const cur = f.terms || "";
              // Remove the template content, then trim leftover blank lines
              const cleaned = cur
                .split(tpl.content)
                .join("")
                .replace(/\n{3,}/g, "\n\n")
                .replace(/^\s+|\s+$/g, "");
              return { ...f, terms: cleaned };
            });
            toast.success(`Plantilla "${tpl.title}" removida`);
          }}
          onClose={() => setTermsModalOpen(false)}
        />
      )}
    </div>
  );
}

function TermsPickerModal({ templates, currentTerms, onPick, onRemove, onClose }) {
  const currentHasText = !!(currentTerms || "").trim();
  const isApplied = (t) => currentTerms.includes(t.content);
  return (
    <div
      className="fixed inset-0 bg-ink-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid="terms-picker-modal"
        className="bg-ink-800 border border-ink-500 w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-500 px-5 py-3">
          <h3 className="font-display font-semibold text-ash-100 flex items-center gap-2">
            <ClipboardList size={16} className="text-brand" /> Plantillas de términos
          </h3>
          <button onClick={onClose} className="p-1 text-ash-300 hover:text-ash-100"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto">
          {templates.length === 0 ? (
            <div className="p-10 text-center text-ash-500 text-sm">
              Aún no hay plantillas creadas.<br />
              El administrador puede crearlas en <span className="font-mono text-brand">Administración → Términos</span>.
            </div>
          ) : (
            <ul>
              {templates.map((t) => {
                const applied = isApplied(t);
                return (
                  <li
                    key={t.id}
                    data-testid={`terms-tpl-${t.id}`}
                    className={`border-b border-ink-500 last:border-0 px-5 py-4 transition-colors ${
                      applied ? "bg-brand/5 border-l-4 border-l-brand" : "hover:bg-ink-700/30"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-display font-semibold text-ash-100">{t.title}</span>
                      {t.is_default && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-brand/15 text-brand border border-brand/30">
                          <Star size={9} fill="currentColor" strokeWidth={0} /> Predeterminada
                        </span>
                      )}
                      {applied && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-brand text-ink-800 ml-auto">
                          <Check size={10} strokeWidth={3} /> Aplicada
                        </span>
                      )}
                    </div>
                    <pre className="text-xs text-ash-300 whitespace-pre-wrap font-sans line-clamp-4 mb-3">{t.content}</pre>
                    {applied ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onRemove(t)}
                          className="border border-[#9B1C1C]/50 hover:border-[#9B1C1C] hover:text-[#FF7B7B] text-ash-300 px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono"
                        >
                          Quitar del campo
                        </button>
                        <span className="text-[10px] text-ash-500 font-mono">Ya está incluida en los términos.</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onPick(t, "replace")}
                          className="bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-3 py-1.5 text-[10px] uppercase tracking-widest"
                        >
                          {currentHasText ? "Reemplazar" : "Usar plantilla"}
                        </button>
                        {currentHasText && (
                          <button
                            onClick={() => onPick(t, "append")}
                            className="border border-ink-500 hover:border-brand hover:text-brand text-ash-300 px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono"
                          >
                            Agregar al final
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-ink-800 border border-ink-500 text-ash-100 focus:border-brand focus:ring-1 focus:ring-brand outline-none px-3 py-2 text-sm font-body placeholder:text-ash-500";

function Section({ title, children, full, action }) {
  return (
    <div className={`bg-ink-800 p-5 md:p-6 ${full ? "border border-ink-500" : ""}`}>
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="text-xs uppercase tracking-widest text-brand font-mono">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function FieldGrid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-ash-300 uppercase tracking-widest mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function PriceInput({ value, onChange, currency, testid }) {
  const isCLP = currency === "CLP";

  const display = (() => {
    if (value === "" || value == null) return "";
    const n = Number(value);
    if (isNaN(n)) return "";
    if (isCLP) {
      return Math.round(n).toLocaleString("es-CL"); // 290.000
    }
    return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  })();

  const handleChange = (e) => {
    const text = e.target.value;
    if (isCLP) {
      const digits = text.replace(/\D/g, "");
      onChange(digits === "" ? "" : Number(digits));
    } else {
      let cleaned = text.replace(/[^\d.]/g, "");
      const parts = cleaned.split(".");
      cleaned =
        parts.length > 1
          ? parts[0] + "." + parts.slice(1).join("").slice(0, 2)
          : parts[0];
      onChange(cleaned === "" ? "" : Number(cleaned));
    }
  };

  return (
    <input
      type="text"
      inputMode={isCLP ? "numeric" : "decimal"}
      data-testid={testid}
      className="w-full bg-ink-800 border border-ink-500 text-ash-100 focus:border-brand focus:ring-1 focus:ring-brand outline-none px-3 py-2 text-sm font-body text-right placeholder:text-ash-500"
      placeholder="0"
      value={display}
      onChange={handleChange}
    />
  );
}
